// Anonymous usage statistics: once a day, a random id made on this phone and a few counts go to /api/usage
// (server/usage.ts), so we know how many people use the app and which parts. Nothing about you, no places,
// nothing from your groups. Can be turned off under More → About.
import { b64uEncode } from "../shared/b64u.ts";
import type { UsageReport } from "../shared/model.ts";
import { $ } from "./dom.ts";
import { LANG } from "./i18n.ts";
import { catches } from "./state.ts";
import { dayKey, lsGet, lsSet } from "./util.ts";
import { groups } from "./groups/store.ts";
import { pins } from "./map/places.ts";
import { hasProfile } from "./tips.ts";

const isOn = () => lsGet("usageOn", "1") === "1";

/** A random id for this installation: not linked to your name, your groups or anything else. */
function installId(): string {
  let id = lsGet("installId", "");
  if (!id) { id = b64uEncode(crypto.getRandomValues(new Uint8Array(16))); lsSet("installId", id); }
  return id;
}

export function platform(): UsageReport["platform"] {
  const ua = navigator.userAgent;
  if (/Android/i.test(ua)) return "android";
  if (/iPhone|iPad|iPod/.test(ua) || (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1)) return "ios";
  if (/Windows|Macintosh|Linux|CrOS/.test(ua)) return "desktop";
  return "other";
}
/** The Android app (Trusted Web Activity), the home-screen app, or a browser tab. */
export function appKind(): UsageReport["app"] {
  if (document.referrer.startsWith("android-app://")) lsSet("fromPlay", "1"); // only on the first page the Android app opens
  if (lsGet("fromPlay", "") === "1") return "play";
  return matchMedia("(display-mode: standalone)").matches || navigator.standalone === true ? "installed" : "browser";
}

/** Sends today's report, if it hasn't gone yet and statistics are on. */
export async function sendUsage(): Promise<void> {
  const today = dayKey(Date.now());
  if (!isOn() || !navigator.onLine || lsGet("usageDay", "") === today) return;
  const report: UsageReport = {
    id: installId(), platform: platform(), app: appKind(), lang: LANG,
    catches: catches.length, pins: pins.length, groups: groups.length,
    pushMsgs: lsGet("pushMsgs", "0") === "1", pushTips: lsGet("pushTips", "0") === "1", profile: hasProfile(),
  };
  try {
    const r = await fetch("/api/usage", { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify(report), signal: AbortSignal.timeout(15000) });
    if (r.ok) lsSet("usageDay", today);
  } catch { /* offline or blocked: try again next time */ }
}

// The switch under More → About.
const box = $<HTMLInputElement>("#usage-on");
box.checked = isOn();
box.addEventListener("change", () => { lsSet("usageOn", box.checked ? "1" : "0"); if (box.checked) void sendUsage(); });

export function usageStart(): void {
  // a little after start, so it never slows the app down; again when the app comes back after midnight
  setTimeout(() => void sendUsage(), 5000);
  document.addEventListener("visibilitychange", () => { if (document.visibilityState === "visible") void sendUsage(); });
}
